<script setup lang="ts">
import {
  EbomPartAllocationRowAllocationMutation,
  EbomPartAllocationRowAllocationMutationVariables,
  EbomPartAllocationRowDataQuery,
  EbomPartAllocationRowDataQueryVariables,
} from '@/generated/graphql'
import { computed, reactive, ref, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { minValue, required } from '@/validation'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import ArticleQuantityField from '@/components/input/ArticleQuantityField.vue'
import AllocateableEbomPartPicker, {
  EbomPartPickerOption,
} from '@/components/input/AllocateableEbomPartPicker.vue'
import SelectField, { SelectOption } from '@/components/input/SelectField.vue'
import IconEbom from '@/components/icons/IconEbom.vue'
import Cross3dModelWarningDialog from '@/components/ebom/Cross3dModelWarningDialog.vue'
import { checkCross3dModelConflict } from '@/composables/useCross3dModelCheck'
import { useSetStepPreferred3dModel } from '@/composables/useSetStepPreferred3dModel'
import { ComponentExposed } from 'vue-component-type-helpers'

const props = defineProps<{
  bopId: string
  stepId: string
  graphNodeStepId?: string
}>()
const emit = defineEmits<{
  allocated: []
}>()

const fetchQuery = useQuery<
  EbomPartAllocationRowDataQuery,
  EbomPartAllocationRowDataQueryVariables
>(
  gql`
    query EbomPartAllocationRowData($bopId: ID!) {
      article {
        ebomsForBop(bopId: $bopId) {
          id
          name
        }
      }
    }
  `,
  () => ({
    bopId: props.bopId,
  }),
)
const ebomOptions = computed<SelectOption<string>[]>(
  () =>
    fetchQuery.result.value?.article.ebomsForBop.map((e) => ({
      title: e.name,
      value: e.id,
    })) || [],
)

const selectedEbomId = ref<string>()
watch(ebomOptions, (v) => {
  if (v.length == 1) {
    selectedEbomId.value = v[0].value
  }
})
watch(selectedEbomId, () => {
  model.ebomPart = undefined
})

type Model = {
  ebomPart?: EbomPartPickerOption
  milliQuantity?: number
}
const model = reactive<Model>({})
watch(
  () => props.stepId,
  () => {
    model.ebomPart = undefined
    model.milliQuantity = undefined
  },
)
const validation = useVuelidate<Model>(
  {
    ebomPart: { required },
    milliQuantity: { required, min: minValue(0) },
  },
  model,
  { $scope: false },
)

const notifySaved = ref(false)
const mutation = useMutation<
  EbomPartAllocationRowAllocationMutation,
  EbomPartAllocationRowAllocationMutationVariables
>(
  gql`
    mutation EbomPartAllocationRowAllocation($command: AllocateEbomPartToStepCommand!) {
      article {
        allocateEbomPartToStep(command: $command)
      }
    }
  `,
  () => ({ refetchQueries: ['EbomPartPickerData'] }),
)
const setStepPreferred3dModel = useSetStepPreferred3dModel()

const warningDialog = ref<ComponentExposed<typeof Cross3dModelWarningDialog>>()

async function setAllocation() {
  validation.value.$touch()
  if (validation.value.$invalid || !selectedEbomId.value) {
    return
  }

  let chosenBopUploadId: string | undefined
  const check = await checkCross3dModelConflict({
    stepId: props.stepId,
    bopId: props.bopId,
    newEbomIds: [selectedEbomId.value],
  })
  if (check.conflict) {
    chosenBopUploadId = await warningDialog.value?.open({
      currentEbomName: check.currentEbomName,
      newEbomName: check.newEbomName,
      currentBopUploadId: check.currentBopUploadId,
      newBopUploadId: check.newBopUploadId,
    })
    if (!chosenBopUploadId) return
  }

  await mutation.mutate({
    command: {
      ebomPartId: model.ebomPart?.id as string,
      stepId: props.stepId,
      milliQuantity: model.milliQuantity as number,
      graphNodeStepId: props.graphNodeStepId,
    },
  })
  if (chosenBopUploadId) {
    await setStepPreferred3dModel(props.stepId, chosenBopUploadId)
  }
  model.ebomPart = undefined
  model.milliQuantity = undefined
  validation.value.$reset()
  notifySaved.value = true
  emit('allocated')
}
</script>

<template>
  <tr>
    <td><icon-ebom /></td>
    <td colspan="2">
      <v-row>
        <v-col cols="6" xl="4">
          <select-field v-model="selectedEbomId" density="compact" :items="ebomOptions" />
        </v-col>
        <v-col>
          <allocateable-ebom-part-picker
            v-if="selectedEbomId"
            :key="selectedEbomId"
            v-model="model.ebomPart"
            :ebom-id="selectedEbomId"
            :step-id="props.stepId"
            :graph-node-step-id="props.graphNodeStepId"
            :validation="validation.part"
            density="compact"
          />
        </v-col>
      </v-row>
    </td>
    <td colspan="2">
      <article-quantity-field
        v-model="model.milliQuantity"
        :validation="validation.milliQuantity"
        density="compact"
      >
        <template #append>
          <v-btn
            density="compact"
            variant="flat"
            color="primary"
            :disabled="validation.$invalid"
            :loading="mutation.loading.value"
            icon="add"
            @click="setAllocation()"
          />
        </template>
      </article-quantity-field>
    </td>
  </tr>

  <notification-saved v-model="notifySaved" />
  <cross3d-model-warning-dialog ref="warningDialog" />
</template>

<style scoped lang="scss">
.form-field {
  margin-bottom: 0.3em;
}
</style>
